/**
 * String Translate Sub-page
 *
 * Renders theme/plugin string translation interface within a tab panel.
 *
 * @package MultilingualPressZone
 * @since 1.1.0
 */

import { el, __, debounce, clear } from '../utils/dom.js';
import API from '../utils/api.js';
import Table from '../components/Table.js';
import Badge from '../components/Badge.js';
import { Button } from '../components/Button.js';
import { Checkbox } from '../components/Checkbox.js';
import { Spinner } from '../components/Spinner.js';
import Modal from '../components/Modal.js';
import Toast from '../components/Toast.js';
import { Skeleton } from '../components/Skeleton.js';
import { FilterBar, FilterGroup } from '../components/FilterBar.js';
import { showTranslatePromoModal } from '../utils/promo-modal.js';
import ProgressBar from '../components/ProgressBar.js';

export default class StringTranslatePage {
    /**
     * @param {HTMLElement} container - Tab content container
     * @param {string} sourceType - 'theme' or 'plugin'
     * @param {Array} languages - Pre-loaded active languages
     */
    constructor(container, sourceType, languages) {
        this.container = container;
        this.sourceType = sourceType;
        this.languages = languages || [];
        this.strings = [];
        this.sources = [];
        this.domains = [];
        this.filters = { source: '', domain: '', search: '', status: '' };
        this.scanning = false;
        this.currentPage = 1;
        this.perPage = 50;
        this.totalPages = 1;
        this.total = 0;
        this.modal = null;
        this.editModalActiveTab = null;
        this.editStringData = null;
        this._destroyed = false;
        this._generating = false;
        this._generatingLang = null;
        this._generatingAll = false;
        this._characterEstimates = {};
        this._generateAllModal = null;
        this._generateAllSelectedLangs = new Set();
        this._generateAllPhase = 'select';
        this._generateAllResult = null;
        this.translateAllInProgress = false;
        this.translateAllProgress = { total: 0, completed: 0, failed: 0, currentLang: null, results: [] };
        this.translateAllProgressBar = null;
        this.tableContainer = null;
        this.paginationContainer = null;
        this.filterCounts = { status: {} };

        this.table = new Table({
            columns: [
                { label: __('String', 'multilingual-press-zone'), key: 'string', width: '30%' },
                { label: __('Domain', 'multilingual-press-zone'), key: 'domain', width: '15%' },
                { label: __('Status', 'multilingual-press-zone'), key: 'status', width: '10%' },
                { label: __('Progress', 'multilingual-press-zone'), key: 'progress', width: '10%' },
                { label: __('Last Updated', 'multilingual-press-zone'), key: 'updated', width: '20%' },
                { label: __('Actions', 'multilingual-press-zone'), key: 'actions', width: '15%' }
            ],
            className: 'presszone-multilingual-table presszone-multilingual-table--legacy mpz-st-table',
            emptyMessage: __('No strings found.', 'multilingual-press-zone')
        });

        this.handleFilterChange = debounce(this._onFilterChange.bind(this), 300);
    }

    async render() {
        if (this._destroyed) return;

        clear(this.container);

        // Show skeleton while loading
        const skeletonContainer = el('div', { class: 'mpz-st-skeleton-container' });
        skeletonContainer.appendChild(Skeleton('row', 8));
        this.container.appendChild(skeletonContainer);

        // Load sources and domains in parallel
        await Promise.all([this.loadSources(), this.loadDomains()]);

        if (this._destroyed) return;

        // Replace skeleton with real UI
        clear(this.container);

        // Filters
        const filters = this.renderFilters();
        this.container.appendChild(filters);

        // Table container
        this.tableContainer = el('div', {
            class: 'mpz-table-container presszone-multilingual-table-container',
            id: `mpz-st-table-container-${this.sourceType}`
        });
        this.container.appendChild(this.tableContainer);

        // Pagination container
        this.paginationContainer = el('div', {
            class: 'mpz-pagination-container',
            id: `mpz-st-pagination-${this.sourceType}`
        });
        this.container.appendChild(this.paginationContainer);

        // Load strings
        await this.loadStrings();
    }

    async loadSources() {
        try {
            const endpoint = this.sourceType === 'theme'
                ? '/system-translate/themes'
                : '/system-translate/plugins';
            const params = {};
            if (this.filters.status) params.status = this.filters.status;
            const data = await API.get(endpoint, params);
            this.sources = data.data || [];
        } catch (error) {
            this.sources = [];
        }
    }

    async loadDomains() {
        try {
            const params = { type: this.sourceType };
            if (this.filters.source) params.name = this.filters.source;
            if (this.filters.status) params.status = this.filters.status;
            const data = await API.get('/system-translate/domains', params);
            this.domains = data.data || [];
        } catch (error) {
            this.domains = [];
        }
    }

    async loadStrings({ silent = false } = {}) {
        if (this._destroyed || !this.tableContainer) return;

        // Show loading skeleton unless silent refresh
        if (!silent) {
            clear(this.tableContainer);
            this.tableContainer.appendChild(Skeleton('row', 8));
        }

        try {
            const params = {
                type: this.sourceType,
                page: this.currentPage,
                per_page: this.perPage,
            };
            if (this.filters.source) params.name = this.filters.source;
            if (this.filters.domain) params.domain = this.filters.domain;
            if (this.filters.search) params.search = this.filters.search;
            if (this.filters.status) params.status = this.filters.status;

            const data = await API.get('/system-translate/strings', params);

            if (this._destroyed) return;

            this.strings = data.data || [];
            this.totalPages = data.pagination?.pages || 1;
            this.currentPage = data.pagination?.page || 1;
            this.total = data.pagination?.total || 0;

            if (data.filter_counts) {
                this.filterCounts = data.filter_counts;
                this.updateStatusFilterCounts();
            }

            // Reload source/domain counts so their dropdowns reflect the current status filter.
            Promise.all([this.loadSources(), this.loadDomains()]).then(() => {
                this.updateSourceDropdown();
                this.updateDomainDropdown();
            });

            this.renderTable();
            this.renderPagination();
            this.updateGenerateAllButton();
        } catch (error) {
            if (this._destroyed) return;
            if (!silent) {
                Toast.error(error?.message || __('Failed to load strings', 'multilingual-press-zone'));
                clear(this.tableContainer);
            }
        }
    }

    renderFilters() {
        const sourceLabel = this.sourceType === 'theme'
            ? __('Theme', 'multilingual-press-zone')
            : __('Plugin', 'multilingual-press-zone');

        const totalSourceStrings = this.sources.reduce((sum, s) => sum + (s.string_count || 0), 0);
        const sourceOptions = [
            el('option', { value: '' }, __('All', 'multilingual-press-zone') + ' ' + sourceLabel + 's' + ` (${totalSourceStrings})`),
            ...this.sources.map(s => el('option', { value: s.slug }, `${s.name} (${s.string_count || 0})`))
        ];

        const sc = this.filterCounts.status || {};
        const totalStrings = (sc.translated || 0) + (sc.partial || 0) + (sc.untranslated || 0);

        const statusOptions = [
            el('option', { value: '' }, __('All Statuses', 'multilingual-press-zone') + ` (${totalStrings})`),
            el('option', { value: 'translated' }, __('Fully Translated', 'multilingual-press-zone') + ` (${sc.translated || 0})`),
            el('option', { value: 'partial' }, __('Partially Translated', 'multilingual-press-zone') + ` (${sc.partial || 0})`),
            el('option', { value: 'untranslated' }, __('Not Translated', 'multilingual-press-zone') + ` (${sc.untranslated || 0})`),
        ];

        return FilterBar({
            groups: [
                FilterGroup({
                    label: __('Search', 'multilingual-press-zone'),
                    forId: `mpz-st-search-${this.sourceType}`,
                    className: 'mpz-filter-group--search',
                    control: el('input', {
                        type: 'text',
                        id: `mpz-st-search-${this.sourceType}`,
                        class: 'mpz-filter-input presszone-multilingual-input--settings presszone-multilingual-input--padding-standard',
                        placeholder: __('Search strings...', 'multilingual-press-zone'),
                        value: this.filters.search,
                        oninput: (e) => {
                            this.filters.search = e.target.value;
                            this.handleFilterChange();
                        }
                    })
                }),
                FilterGroup({
                    label: sourceLabel,
                    forId: `mpz-st-source-${this.sourceType}`,
                    control: el('select', {
                        id: `mpz-st-source-${this.sourceType}`,
                        class: 'mpz-filter-select presszone-multilingual-select--settings presszone-multilingual-select--padding-standard',
                        value: this.filters.source,
                        onchange: (e) => {
                            this.filters.source = e.target.value;
                            this.filters.domain = '';
                            this.loadDomains().then(() => this.updateDomainDropdown());
                            this.handleFilterChange();
                        }
                    }, ...sourceOptions)
                }),
                FilterGroup({
                    label: __('Domain', 'multilingual-press-zone'),
                    forId: `mpz-st-domain-${this.sourceType}`,
                    control: el('select', {
                        id: `mpz-st-domain-${this.sourceType}`,
                        class: 'mpz-filter-select presszone-multilingual-select--settings presszone-multilingual-select--padding-standard',
                        value: this.filters.domain,
                        onchange: (e) => {
                            this.filters.domain = e.target.value;
                            this.handleFilterChange();
                        }
                    },
                        el('option', { value: '' }, __('All Domains', 'multilingual-press-zone') + ` (${this.getDomainTotal()})`),
                        ...this.domains.map(d => {
                            const domain = typeof d === 'object' ? d.domain : d;
                            const count = typeof d === 'object' ? (d.string_count || 0) : 0;
                            return el('option', { value: domain }, `${domain} (${count})`);
                        })
                    )
                }),
                FilterGroup({
                    label: __('Status', 'multilingual-press-zone'),
                    forId: `mpz-st-status-${this.sourceType}`,
                    control: el('select', {
                        id: `mpz-st-status-${this.sourceType}`,
                        class: 'mpz-filter-select presszone-multilingual-select--settings presszone-multilingual-select--padding-standard',
                        value: this.filters.status,
                        onchange: (e) => {
                            this.filters.status = e.target.value;
                            this.handleFilterChange();
                        }
                    }, ...statusOptions)
                }),
                el('div', { class: 'mpz-filter-group mpz-filter-group--actions' },
                    Button({
                        label: this.scanning
                            ? __('Scanning...', 'multilingual-press-zone')
                            : __('Refresh Scan', 'multilingual-press-zone'),
                        variant: 'secondary',
                        size: 'sm',
                        disabled: this.scanning,
                        attrs: { id: `mpz-st-scan-btn-${this.sourceType}` },
                        onClick: () => this.triggerScan()
                    }),
                    Button({
                        label: this._generatingAll
                            ? __('Generating...', 'multilingual-press-zone')
                            : __('Generate All', 'multilingual-press-zone'),
                        variant: 'secondary',
                        size: 'sm',
                        disabled: this._generatingAll || !window.multilingualPressZone?.translate?.available,
                        attrs: { id: `mpz-st-generate-all-btn-${this.sourceType}` },
                        onClick: () => this.generateAll()
                    })
                )
            ]
        });
    }

    getDomainTotal() {
        return this.domains.reduce((sum, d) => sum + (typeof d === 'object' ? (d.string_count || 0) : 0), 0);
    }

    updateStatusFilterCounts() {
        const sc = this.filterCounts.status || {};
        const totalStrings = (sc.translated || 0) + (sc.partial || 0) + (sc.untranslated || 0);

        const statusSelect = document.getElementById(`mpz-st-status-${this.sourceType}`);
        if (statusSelect) {
            const labels = [
                __('All Statuses', 'multilingual-press-zone') + ` (${totalStrings})`,
                __('Fully Translated', 'multilingual-press-zone') + ` (${sc.translated || 0})`,
                __('Partially Translated', 'multilingual-press-zone') + ` (${sc.partial || 0})`,
                __('Not Translated', 'multilingual-press-zone') + ` (${sc.untranslated || 0})`,
            ];
            Array.from(statusSelect.options).forEach((opt, i) => {
                if (i < labels.length) opt.textContent = labels[i];
            });
        }
    }

    updateSourceDropdown() {
        const sourceSelect = document.getElementById(`mpz-st-source-${this.sourceType}`);
        if (!sourceSelect) return;
        const sourceLabel = this.sourceType === 'theme'
            ? __('Themes', 'multilingual-press-zone')
            : __('Plugins', 'multilingual-press-zone');
        const totalSourceStrings = this.sources.reduce((sum, s) => sum + (s.string_count || 0), 0);
        while (sourceSelect.firstChild) sourceSelect.removeChild(sourceSelect.firstChild);
        sourceSelect.appendChild(el('option', { value: '' }, __('All', 'multilingual-press-zone') + ' ' + sourceLabel + ` (${totalSourceStrings})`));
        this.sources.forEach(s => {
            const opt = el('option', { value: s.slug }, `${s.name} (${s.string_count || 0})`);
            if (s.slug === this.filters.source) opt.selected = true;
            sourceSelect.appendChild(opt);
        });
    }

    updateDomainDropdown() {
        const domainSelect = document.getElementById(`mpz-st-domain-${this.sourceType}`);
        if (!domainSelect) return;

        // Clear using DOM methods
        while (domainSelect.firstChild) {
            domainSelect.removeChild(domainSelect.firstChild);
        }
        domainSelect.appendChild(el('option', { value: '' }, __('All Domains', 'multilingual-press-zone') + ` (${this.getDomainTotal()})`));
        this.domains.forEach(d => {
            const domain = typeof d === 'object' ? d.domain : d;
            const count = typeof d === 'object' ? (d.string_count || 0) : 0;
            domainSelect.appendChild(el('option', { value: domain }, `${domain} (${count})`));
        });
    }

    renderTable() {
        if (!this.tableContainer) return;
        clear(this.tableContainer);

        if (this.strings.length === 0) {
            this.tableContainer.appendChild(
                el('div', { class: 'presszone-multilingual-empty-state' },
                    el('p', {}, this.scanning
                        ? __('Scanning in progress...', 'multilingual-press-zone')
                        : __('No strings found. Click "Refresh Scan" to scan for translatable strings.', 'multilingual-press-zone')
                    )
                )
            );
            return;
        }

        const tableEl = this.table.render(this.strings, (stringData) => this.renderRow(stringData));
        this.tableContainer.appendChild(tableEl);
    }

    renderRow(stringData) {
        const truncated = stringData.original_string.length > 80
            ? stringData.original_string.substring(0, 80) + '...'
            : stringData.original_string;

        const { translated, total } = stringData.translations || { translated: 0, total: 0 };
        const progressPct = total > 0 ? Math.round((translated / total) * 100) : 0;
        let badgeVariant = 'default';
        if (progressPct === 100) badgeVariant = 'success';
        else if (progressPct > 0) badgeVariant = 'warning';

        let statusText = __('Not Translated', 'multilingual-press-zone');
        let statusVariant = 'default';
        if (progressPct === 100) {
            statusText = __('Translated', 'multilingual-press-zone');
            statusVariant = 'success';
        } else if (progressPct > 0) {
            statusText = __('Partial', 'multilingual-press-zone');
            statusVariant = 'warning';
        }

        const row = el('tr', {
            class: 'mpz-st-table-row',
            'data-string-id': stringData.id,
            onclick: () => this.openEditModal(stringData)
        },
            el('td', {},
                el('span', {
                    class: 'mpz-st-string',
                    title: stringData.original_string
                }, truncated)
            ),
            el('td', {},
                el('span', { class: 'mpz-st-domain' }, stringData.domain)
            ),
            el('td', {},
                new Badge({ text: statusText, variant: statusVariant }).getElement()
            ),
            el('td', {},
                new Badge({ text: `${translated}/${total}`, variant: badgeVariant }).getElement()
            ),
            el('td', {},
                (() => {
                    const lastAt = stringData.last_translated_at;
                    const byName = stringData.last_translated_by_name || '';
                    const isAuto = stringData.last_is_auto;
                    if (!lastAt) {
                        return el('span', { class: 'mpz-ct-date' }, '-');
                    }
                    const tooltipLines = [
                        `${__('Last Updated:', 'multilingual-press-zone')} ${new Date(lastAt).toLocaleString()}`,
                        byName ? `${__('By:', 'multilingual-press-zone')} ${byName}` : '',
                        isAuto ? __('Auto-generated', 'multilingual-press-zone') : '',
                    ].filter(Boolean).join('\n');
                    return el('span', { class: 'mpz-tooltip-wrap' },
                        el('span', { class: 'mpz-ct-date' }, this.formatDate(lastAt)),
                        el('span', { class: 'mpz-tooltip-box', style: 'white-space: pre' }, tooltipLines)
                    );
                })()
            ),
            el('td', {},
                el('div', { class: 'mpz-table-actions' },
                    Button({
                        label: __('Translate', 'multilingual-press-zone'),
                        variant: 'primary',
                        size: 'sm',
                        onClick: (e) => {
                            e.stopPropagation();
                            this.openEditModal(stringData);
                        }
                    }),
                    Button({
                        label: __('Generate', 'multilingual-press-zone'),
                        variant: 'secondary',
                        size: 'sm',
                        onClick: (e) => {
                            e.stopPropagation();
                            this.handleGenerate(stringData);
                        }
                    })
                )
            )
        );

        return row;
    }

    renderPagination() {
        if (!this.paginationContainer) return;
        clear(this.paginationContainer);

        if (this.totalPages <= 1) return;

        this.paginationContainer.appendChild(
            el('div', { class: 'mpz-pagination' },
                Button({
                    label: __('Previous', 'multilingual-press-zone'),
                    variant: 'ghost',
                    size: 'sm',
                    onClick: () => this.goToPage(this.currentPage - 1),
                    disabled: this.currentPage === 1
                }),
                el('span', { class: 'mpz-pagination__info' },
                    `${__('Page', 'multilingual-press-zone')} ${this.currentPage} ${__('of', 'multilingual-press-zone')} ${this.totalPages}`
                ),
                Button({
                    label: __('Next', 'multilingual-press-zone'),
                    variant: 'ghost',
                    size: 'sm',
                    onClick: () => this.goToPage(this.currentPage + 1),
                    disabled: this.currentPage === this.totalPages
                })
            )
        );
    }

    goToPage(page) {
        if (page < 1 || page > this.totalPages) return;
        this.currentPage = page;
        this.loadStrings();
    }

    _onFilterChange() {
        this.currentPage = 1;
        this.loadStrings();
    }

    async triggerScan() {
        if (this.scanning) return;

        this.scanning = true;
        this.updateScanButton();

        try {
            const params = { type: this.sourceType };
            if (this.filters.source) {
                params.name = this.filters.source;
            }

            Toast.info(__('Scanning for translatable strings...', 'multilingual-press-zone'));
            const result = await API.post('/system-translate/scan', params);

            if (this._destroyed) return;

            const data = result.data || {};
            Toast.success(
                `${__('Scan complete:', 'multilingual-press-zone')} ${data.strings_found || 0} ${__('strings found', 'multilingual-press-zone')}, ${data.strings_new || 0} ${__('new', 'multilingual-press-zone')}`
            );

            // Reload domains and strings
            await this.loadDomains();
            this.updateDomainDropdown();
            await this.loadStrings();
        } catch (error) {
            if (this._destroyed) return;
            Toast.error(error?.message || __('Scan failed', 'multilingual-press-zone'));
        } finally {
            this.scanning = false;
            this.updateScanButton();
        }
    }

    updateScanButton() {
        const btn = document.getElementById(`mpz-st-scan-btn-${this.sourceType}`);
        if (btn) {
            btn.disabled = this.scanning;
            btn.textContent = this.scanning
                ? __('Scanning...', 'multilingual-press-zone')
                : __('Refresh Scan', 'multilingual-press-zone');
        }
    }

    // --- Edit Modal ---

    async openEditModal(stringData) {
        try {
            // Fetch full details with language_details
            const response = await API.get(`/system-translate/strings/${stringData.id}`);
            this.editStringData = response.data;

            if (!this.editStringData || !this.editStringData.language_details) {
                Toast.error(__('Failed to load string details', 'multilingual-press-zone'));
                return;
            }

            // Default to first active untranslated language, then any untranslated, then first language
            const firstUntranslated = this.editStringData.language_details.find(
                l => l.translation_status !== 'translated' && l.is_active !== false
            ) || this.editStringData.language_details.find(
                l => l.translation_status !== 'translated'
            );
            this.editModalActiveTab = firstUntranslated
                ? firstUntranslated.language_code
                : this.editStringData.language_details[0]?.language_code;

            // Reset generate state
            this._generating = false;
            this._generatingLang = null;
            this._characterEstimates = {};
            this.translateAllInProgress = false;
            this.translateAllProgress = { total: 0, completed: 0, failed: 0, currentLang: null, results: [] };
            this.translateAllProgressBar = null;

            this.modal = new Modal({
                title: __('Edit Translation', 'multilingual-press-zone'),
                width: '900px',
                id: 'mpz-string-edit-modal'
            });

            const content = this.renderEditModalContent();
            const tabs = this.renderEditModalTabs();
            const actions = this.renderEditModalActions();

            this.modal.render(content, actions, tabs);
            this.modal.open();

            // Non-blocking: fetch character estimates for active untranslated languages only
            if (window.multilingualPressZone?.translate?.available) {
                const untranslatedLangs = this.editStringData.language_details
                    .filter(l => l.translation_status !== 'translated' && l.is_active !== false)
                    .map(l => l.language_code);
                if (untranslatedLangs.length > 0) {
                    this.fetchCharacterEstimates(this.editStringData.id, untranslatedLangs);
                }
            }
        } catch (error) {
            Toast.error(error?.message || __('Failed to load string details', 'multilingual-press-zone'));
        }
    }

    renderEditModalTabs() {
        if (!this.editStringData?.language_details) return null;

        return this.editStringData.language_details.map(lang => {
            const isActive = lang.language_code === this.editModalActiveTab;
            const isTranslated = lang.translation_status === 'translated';
            const isInactive = lang.is_active === false;
            const flag = this.getFlagEmoji(lang.flag_code || lang.language_code);

            const classes = [
                'presszone-multilingual-tabs__item',
                isActive ? 'presszone-multilingual-tabs__item--active' : '',
                isInactive ? 'presszone-multilingual-tabs__item--inactive' : '',
            ].filter(Boolean).join(' ');

            return el('button', {
                type: 'button',
                role: 'tab',
                'aria-selected': isActive ? 'true' : 'false',
                class: classes,
                'data-tab': lang.language_code,
                onclick: (e) => {
                    e.preventDefault();
                    this.editModalActiveTab = lang.language_code;
                    this.modal.updateContent(
                        this.renderEditModalContent(),
                        this.renderEditModalTabs()
                    );
                    this.modal.updateActions(this.renderEditModalActions());
                }
            },
                el('span', {}, `${flag} ${lang.native_name || lang.name}`),
                isTranslated ? el('span', { class: 'mpz-st-tab-check', 'aria-label': __('Translated', 'multilingual-press-zone') }, ' \u2713') : null,
                isInactive ? el('span', {
                    class: 'mpz-st-tab-inactive-badge',
                    'aria-label': __('Inactive', 'multilingual-press-zone')
                }, __('inactive', 'multilingual-press-zone')) : null
            );
        });
    }

    renderEditModalContent() {
        if (!this.editStringData) return el('div');

        const lang = this.editStringData.language_details.find(
            l => l.language_code === this.editModalActiveTab
        );

        const currentTranslation = lang?.translated_string || '';
        const status = lang?.translation_status || 'pending';

        // Resolve source language name from the languages list
        const defaultLang = this.languages.find(l => l.is_default || l.isDefault);
        const sourceName = defaultLang?.native_name || defaultLang?.nativeName || defaultLang?.name || '';
        const originalLabel = sourceName
            ? `${__('Original String', 'multilingual-press-zone')} (${sourceName})`
            : __('Original String', 'multilingual-press-zone');

        // Determine text direction for the target language
        const langDir = lang?.text_direction === 'rtl' ? 'rtl' : 'ltr';

        return el('div', { class: 'mpz-st-edit-layout' },
            el('div', { class: 'mpz-st-edit-grid' },
                el('div', { class: 'mpz-st-edit-grid__original' },
                    el('label', { class: 'mpz-st-edit-label' }, originalLabel),
                    el('div', { class: 'mpz-st-edit-grid__original-text' },
                        this.editStringData.original_string
                    )
                ),
                (() => {
                    if (this._generating && this._generatingLang === this.editModalActiveTab) {
                        return el('div', { class: 'mpz-st-edit-grid__translation' },
                            el('label', { class: 'mpz-st-edit-label' },
                                __('Translation', 'multilingual-press-zone')),
                            el('div', { class: 'mpz-ct-generating' },
                                el('div', { class: 'mpz-ct-generating__spinner' }),
                                el('p', { class: 'mpz-ct-generating__text' },
                                    __('Generating translation...', 'multilingual-press-zone'))
                            )
                        );
                    }
                    return el('div', { class: 'mpz-st-edit-grid__translation' },
                        el('label', {
                            class: 'mpz-st-edit-label',
                            for: 'mpz-st-translation-textarea'
                        }, __('Translation', 'multilingual-press-zone') + ` (${lang?.native_name || lang?.name || ''})`),
                        (() => {
                            const textarea = el('textarea', {
                                id: 'mpz-st-translation-textarea',
                                dir: langDir,
                                placeholder: __('Enter translation...', 'multilingual-press-zone'),
                            });
                            textarea.value = currentTranslation;
                            return textarea;
                        })()
                    );
                })()
            ),
            el('div', { class: 'mpz-st-edit-meta' },
                el('span', {}, `${__('Domain:', 'multilingual-press-zone')} ${this.editStringData.domain}`),
                el('span', {}, `${__('Status:', 'multilingual-press-zone')} `),
                Badge.fromStatus(status).getElement(),
                el('span', {}, lang?.updated_at
                    ? `${__('Last Updated:', 'multilingual-press-zone')} ${new Date(lang.updated_at).toLocaleString()}`
                    : __('Last Updated: Never', 'multilingual-press-zone')
                ),
                lang?.updated_at && lang?.translated_by_name ? el('span', {}, `${__('By:', 'multilingual-press-zone')} ${lang.translated_by_name}`) : null,
                lang?.updated_at && lang?.is_auto_translated ? el('span', { class: 'mpz-st-meta-auto' }, __('Auto-generated', 'multilingual-press-zone')) : null
            )
        );
    }

    renderEditModalActions() {
        // During translate-all — no action buttons
        if (this.translateAllInProgress) {
            return null;
        }

        const actions = [
            Button({
                label: __('Close', 'multilingual-press-zone'),
                variant: 'ghost',
                onClick: () => this.modal.close()
            }),
        ];

        if (window.multilingualPressZone?.translate?.available) {
            const missingLangs = this.getMissingLanguages();
            const estimate = this._characterEstimates?.[this.editModalActiveTab];
            const charLabel = estimate ? ` (~${estimate.estimated_characters} chars)` : '';

            // "Translate All (N)" when 2+ languages are missing
            if (missingLangs.length > 1) {
                actions.push(Button({
                    label: __('Translate All (%d)', 'multilingual-press-zone').replace('%d', missingLangs.length),
                    variant: 'primary',
                    onClick: () => this.translateAllMissing()
                }));
                actions.push(Button({
                    label: this._generating
                        ? __('Generating...', 'multilingual-press-zone')
                        : __('Generate', 'multilingual-press-zone') + charLabel,
                    variant: 'secondary',
                    disabled: this._generating,
                    onClick: () => this.generateTranslation(this.editModalActiveTab)
                }));
            } else {
                actions.push(Button({
                    label: this._generating
                        ? __('Generating...', 'multilingual-press-zone')
                        : __('Generate', 'multilingual-press-zone') + charLabel,
                    variant: 'primary',
                    disabled: this._generating,
                    onClick: () => this.generateTranslation(this.editModalActiveTab)
                }));
            }
        }

        actions.push(Button({
            label: this._saving
                ? __('Saving...', 'multilingual-press-zone')
                : __('Save', 'multilingual-press-zone'),
            variant: 'secondary',
            disabled: this._saving,
            onClick: () => this.saveTranslation()
        }));

        return actions;
    }

    handleGenerate(stringData) {
        if (window.multilingualPressZone?.translate?.available) {
            this.openEditModal(stringData);
        } else {
            showTranslatePromoModal();
        }
    }

    async generateTranslation(targetLang) {
        if (!this.editStringData || this._generating) return;

        this._generating = true;
        this._generatingLang = targetLang;

        // Update modal UI to show generating state
        this.modal.updateContent(
            this.renderEditModalContent(),
            this.renderEditModalTabs()
        );
        this.modal.updateActions(this.renderEditModalActions());

        try {
            const result = await API.post('/translations/generate', {
                string_id: this.editStringData.id,
                target_lang: targetLang,
                content_type: 'string',
            });

            Toast.success(__('Translation generated successfully', 'multilingual-press-zone'));

            // Update local data with result
            this.editStringData = result.data;

            // Update the row in the table immediately
            this.updateRowProgress(this.editStringData);

            // Silently refresh the full list to update filter counts and remove
            // rows that no longer match the active status filter
            this.loadStrings({ silent: true });
        } catch (error) {
            Toast.error(error?.message || __('Failed to generate translation', 'multilingual-press-zone'));
        } finally {
            this._generating = false;
            this._generatingLang = null;

            // Update modal with results (textarea now has the generated text)
            this.modal.updateContent(
                this.renderEditModalContent(),
                this.renderEditModalTabs()
            );
            this.modal.updateActions(this.renderEditModalActions());
        }
    }

    async fetchCharacterEstimates(stringId, targetLangs) {
        try {
            const result = await API.post('/translations/estimate-characters', {
                string_id: stringId,
                target_langs: targetLangs,
            });

            if (result?.data?.estimates) {
                this._characterEstimates = result.data.estimates;
                // Refresh modal actions to show character counts
                if (this.modal?.modalEl) {
                    this.modal.updateActions(this.renderEditModalActions());
                }
            }
        } catch (error) {
            // Non-blocking — silently ignore estimate failures
        }
    }

    async saveTranslation() {
        const textarea = document.getElementById('mpz-st-translation-textarea');
        if (!textarea || !this.editStringData) return;

        const translatedString = textarea.value;

        this._saving = true;
        this.modal.updateActions(this.renderEditModalActions());

        try {
            const result = await API.put(
                `/system-translate/strings/${this.editStringData.id}`,
                {
                    language_code: this.editModalActiveTab,
                    translated_string: translatedString,
                }
            );

            Toast.success(__('Translation saved', 'multilingual-press-zone'));

            // Update local data
            this.editStringData = result.data;

            // Update the row in the table immediately
            this.updateRowProgress(this.editStringData);

            // Silently refresh the full list to update filter counts
            this.loadStrings({ silent: true });

            // Advance to next untranslated language if current was empty
            const nextUntranslated = this.editStringData.language_details.find(
                l => l.language_code !== this.editModalActiveTab && l.translation_status !== 'translated'
            );
            if (nextUntranslated) {
                this.editModalActiveTab = nextUntranslated.language_code;
            }

            // Re-render modal content
            this.modal.updateContent(
                this.renderEditModalContent(),
                this.renderEditModalTabs()
            );
        } catch (error) {
            Toast.error(error?.message || __('Failed to save translation', 'multilingual-press-zone'));
        } finally {
            this._saving = false;
            this.modal.updateActions(this.renderEditModalActions());
        }
    }

    updateRowProgress(stringData) {
        if (!stringData?.translations) return;

        // Update the in-memory strings array
        const idx = this.strings.findIndex(s => s.id === stringData.id);
        if (idx !== -1) {
            this.strings[idx].translations = stringData.translations;
            this.strings[idx].last_translated_at = stringData.last_translated_at;
            this.strings[idx].last_translated_by_name = stringData.last_translated_by_name;
            this.strings[idx].last_is_auto = stringData.last_is_auto;
        }

        // Update the DOM row's status and progress badges
        const row = this.tableContainer?.querySelector(`tr[data-string-id="${stringData.id}"]`);
        if (row) {
            const tds = row.querySelectorAll('td');
            const { translated, total } = stringData.translations;
            const progressPct = total > 0 ? Math.round((translated / total) * 100) : 0;
            let badgeVariant = 'default';
            if (progressPct === 100) badgeVariant = 'success';
            else if (progressPct > 0) badgeVariant = 'warning';

            let statusText = __('Not Translated', 'multilingual-press-zone');
            let statusVariant = 'default';
            if (progressPct === 100) {
                statusText = __('Translated', 'multilingual-press-zone');
                statusVariant = 'success';
            } else if (progressPct > 0) {
                statusText = __('Partial', 'multilingual-press-zone');
                statusVariant = 'warning';
            }

            // Status column (index 2)
            const statusTd = tds[2];
            if (statusTd) {
                clear(statusTd);
                statusTd.appendChild(new Badge({ text: statusText, variant: statusVariant }).getElement());
            }

            // Progress column (index 3)
            const progressTd = tds[3];
            if (progressTd) {
                clear(progressTd);
                progressTd.appendChild(new Badge({ text: `${translated}/${total}`, variant: badgeVariant }).getElement());
            }

            // Last Updated column (index 4)
            const dateTd = tds[4];
            if (dateTd) {
                clear(dateTd);
                const lastAt = stringData.last_translated_at;
                const byName = stringData.last_translated_by_name || '';
                const isAuto = stringData.last_is_auto;
                if (!lastAt) {
                    dateTd.appendChild(el('span', { class: 'mpz-ct-date' }, '-'));
                } else {
                    const tooltipLines = [
                        `${__('Last Updated:', 'multilingual-press-zone')} ${new Date(lastAt).toLocaleString()}`,
                        byName ? `${__('By:', 'multilingual-press-zone')} ${byName}` : '',
                        isAuto ? __('Auto-generated', 'multilingual-press-zone') : '',
                    ].filter(Boolean).join('\n');
                    dateTd.appendChild(el('span', { class: 'mpz-tooltip-wrap' },
                        el('span', { class: 'mpz-ct-date' }, this.formatDate(lastAt)),
                        el('span', { class: 'mpz-tooltip-box', style: 'white-space: pre' }, tooltipLines)
                    ));
                }
            }
        }
    }

    formatDate(dateString) {
        if (!dateString) return '-';

        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return __('Just now', 'multilingual-press-zone');
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;

        return date.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    }

    getFlagEmoji(code) {
        if (!code || code.length !== 2) return '\uD83C\uDF10'; // globe
        const codePoints = code
            .toUpperCase()
            .split('')
            .map(char => 127397 + char.charCodeAt(0));
        return String.fromCodePoint(...codePoints);
    }

    // =========================================================================
    // Translate All (multi-language within edit modal)
    // =========================================================================

    getMissingLanguages() {
        if (!this.editStringData?.language_details) return [];
        return this.editStringData.language_details
            .filter(l => l.translation_status !== 'translated')
            .map(l => l.language_code);
    }

    async translateAllMissing() {
        if (!this.editStringData) return;
        const missingLangs = this.getMissingLanguages();
        if (missingLangs.length === 0) return;

        this.translateAllInProgress = true;
        this.translateAllProgress = {
            total: missingLangs.length,
            completed: 0,
            failed: 0,
            currentLang: missingLangs[0],
            results: []
        };

        this.renderTranslateAllProgressView();

        for (const lang of missingLangs) {
            this.translateAllProgress.currentLang = lang;
            this.updateTranslateAllProgressLabel();

            try {
                const result = await API.post('/translations/generate', {
                    string_id: this.editStringData.id,
                    target_lang: lang,
                    content_type: 'string',
                });
                this.translateAllProgress.completed++;
                this.translateAllProgress.results.push({ lang, status: 'completed' });

                // Merge updated data if returned
                if (result?.data) {
                    this.editStringData = result.data;
                } else {
                    // Mark locally as translated for tab indicator
                    const langDetail = this.editStringData.language_details.find(l => l.language_code === lang);
                    if (langDetail) {
                        langDetail.translation_status = 'translated';
                    }
                }
            } catch (error) {
                this.translateAllProgress.failed++;
                this.translateAllProgress.results.push({ lang, status: 'failed', error: error.message });

                const msg = (error.message || '').toLowerCase();
                if (msg.includes('license') || msg.includes('quota') || msg.includes('credits') || msg.includes('limit')) {
                    Toast.error(error.message);
                    break;
                }
            }

            // Update progress bar value (bar stays in the same DOM node — no re-render)
            if (this.translateAllProgressBar) {
                this.translateAllProgressBar.setValue(
                    this.translateAllProgress.completed + this.translateAllProgress.failed
                );
            }

            // Refresh only tabs; reuse the same content element to keep the ProgressBar intact
            if (this.modal?.isOpen && this._translateAllProgressEl) {
                this.modal.updateContent(
                    this._translateAllProgressEl,
                    this.renderTranslateAllTabs()
                );
            }
        }

        this.translateAllInProgress = false;

        // Update table row
        this.updateRowProgress(this.editStringData);

        // Restore normal edit modal so user can see the translations immediately
        this._translateAllProgressEl = null;
        const firstTranslated = this.translateAllProgress.results.find(r => r.status === 'completed');
        if (firstTranslated) {
            this.editModalActiveTab = firstTranslated.lang;
        }
        if (this.modal?.isOpen) {
            this.modal.updateContent(
                this.renderEditModalContent(),
                this.renderEditModalTabs()
            );
            this.modal.updateActions(this.renderEditModalActions());
        }
    }

    renderTranslateAllProgressView() {
        if (!this.modal?.isOpen) return;

        // Store the element reference so the loop can reuse it (avoids detaching the ProgressBar)
        this._translateAllProgressEl = this.renderTranslateAllProgressContent();
        this.modal.updateContent(this._translateAllProgressEl, this.renderTranslateAllTabs());
        this.modal.updateActions(null);

        const barContainer = this._translateAllProgressEl.querySelector('.mpz-translate-all-progress__bar');
        if (barContainer) {
            this.translateAllProgressBar = new ProgressBar({
                container: barContainer,
                value: 0,
                max: this.translateAllProgress.total,
                animated: true,
                showPercentage: true,
                status: 'in-progress'
            });
        }
    }

    renderTranslateAllProgressContent() {
        const { currentLang, completed, failed, total } = this.translateAllProgress;
        const langDetail = this.editStringData?.language_details?.find(l => l.language_code === currentLang);
        const langName = langDetail?.native_name || langDetail?.name || (currentLang ? currentLang.toUpperCase() : '');
        const current = Math.min(completed + failed + 1, total);

        return el('div', { class: 'mpz-bulk-progress-container' },
            el('div', {
                class: 'mpz-bulk-progress-container__label',
                'data-translate-all-label': 'true'
            },
                __('Translating to %1$s (%2$d/%3$d)', 'multilingual-press-zone')
                    .replace('%1$s', langName)
                    .replace('%2$d', current)
                    .replace('%3$d', total)
            ),
            el('div', { class: 'mpz-translate-all-progress__bar mpz-bulk-progress-container__bar' })
        );
    }

    updateTranslateAllProgressLabel() {
        if (!this.modal?.isOpen) return;
        const label = this.modal.modalEl?.querySelector('[data-translate-all-label]');
        if (!label) return;

        const { currentLang, completed, failed, total } = this.translateAllProgress;
        const langDetail = this.editStringData?.language_details?.find(l => l.language_code === currentLang);
        const langName = langDetail?.native_name || langDetail?.name || currentLang.toUpperCase();
        const current = Math.min(completed + failed + 1, total);

        label.textContent = __('Translating to %1$s (%2$d/%3$d)', 'multilingual-press-zone')
            .replace('%1$s', langName)
            .replace('%2$d', current)
            .replace('%3$d', total);
    }

    renderTranslateAllTabs() {
        if (!this.editStringData?.language_details) return [];

        const resultMap = new Map(
            this.translateAllProgress.results.map(r => [r.lang, r.status])
        );

        return this.editStringData.language_details.map(lang => {
            const code = lang.language_code;
            const flag = this.getFlagEmoji(lang.flag_code || code);
            const isInactive = lang.is_active === false;
            const translateAllResult = resultMap.get(code);
            const isCurrent = code === this.translateAllProgress.currentLang && !translateAllResult;

            const classes = [
                'presszone-multilingual-tabs__item',
                isCurrent ? 'presszone-multilingual-tabs__item--active' : '',
                isInactive ? 'presszone-multilingual-tabs__item--inactive' : ''
            ].filter(Boolean).join(' ');

            let statusIndicator = null;
            if (translateAllResult === 'completed' || lang.translation_status === 'translated') {
                statusIndicator = el('span', {
                    class: 'mpz-st-tab-check',
                    'aria-label': __('Translated', 'multilingual-press-zone')
                }, ' \u2713');
            } else if (translateAllResult === 'failed') {
                statusIndicator = el('span', {
                    class: 'mpz-st-tab-check mpz-st-tab-check--failed',
                    'aria-label': __('Failed', 'multilingual-press-zone')
                }, ' \u2717');
            } else if (isInactive) {
                statusIndicator = el('span', {
                    class: 'mpz-st-tab-inactive-badge',
                    'aria-label': __('Inactive', 'multilingual-press-zone')
                }, __('inactive', 'multilingual-press-zone'));
            }

            return el('button', {
                type: 'button',
                role: 'tab',
                'aria-selected': isCurrent ? 'true' : 'false',
                class: classes,
                disabled: true
            },
                el('span', {}, `${flag} ${lang.native_name || lang.name}`),
                statusIndicator
            );
        });
    }

    renderTranslateAllCompletion() {
        if (!this.modal?.isOpen) return;

        const { completed, failed, total, results } = this.translateAllProgress;
        const hasFailures = failed > 0;
        const iconText = hasFailures ? '\u26A0' : '\u2713';
        const iconClass = hasFailures
            ? 'tpz-modal-progress__icon tpz-modal-progress__icon--error'
            : 'tpz-modal-progress__icon';

        const summaryText = hasFailures
            ? __('%1$d of %2$d languages translated', 'multilingual-press-zone')
                .replace('%1$d', completed)
                .replace('%2$d', total)
            : __('All %d languages translated', 'multilingual-press-zone')
                .replace('%d', completed);

        const resultItems = results.map(r => {
            const langDetail = this.editStringData?.language_details?.find(l => l.language_code === r.lang);
            const langName = langDetail?.native_name || langDetail?.name || r.lang.toUpperCase();
            const flag = this.getFlagEmoji(langDetail?.flag_code || r.lang);
            const isOk = r.status === 'completed';

            return el('div', { class: 'mpz-translate-all-result' },
                el('span', { class: 'mpz-translate-all-result__lang' }, `${flag} ${langName}`),
                el('span', {
                    class: `mpz-translate-all-result__status mpz-translate-all-result__status--${isOk ? 'ok' : 'fail'}`
                }, isOk ? '\u2713' : '\u2717'),
                !isOk ? el('span', { class: 'mpz-translate-all-result__error' }, r.error || '') : null
            );
        });

        const content = el('div', { class: 'tpz-modal-progress tpz-modal-progress--done' },
            el('div', { class: iconClass }, iconText),
            el('h3', { class: 'tpz-modal-progress__title' }, summaryText),
            el('div', { class: 'mpz-translate-all-results' }, ...resultItems)
        );

        this.modal.updateContent(content, this.renderTranslateAllTabs());
        this.modal.updateActions(
            Button({
                label: __('Close', 'multilingual-press-zone'),
                variant: 'primary',
                onClick: () => this.modal.close()
            })
        );
    }

    updateGenerateAllButton() {
        const btn = document.getElementById(`mpz-st-generate-all-btn-${this.sourceType}`);
        if (btn) {
            btn.disabled = this._generatingAll || !window.multilingualPressZone?.translate?.available;
            btn.textContent = this._generatingAll
                ? __('Generating...', 'multilingual-press-zone')
                : __('Generate All', 'multilingual-press-zone');
        }
    }

    generateAll() {
        if (this._generatingAll) return;

        if (!window.multilingualPressZone?.translate?.available) {
            showTranslatePromoModal();
            return;
        }

        const activeLangs = this.languages.filter(l => !l.is_default);

        if (activeLangs.length === 0) {
            Toast.error(__('No active languages configured.', 'multilingual-press-zone'));
            return;
        }

        // Pre-select only languages that have untranslated strings
        const perLang = this.filterCounts.per_language_untranslated || {};
        this._generateAllSelectedLangs = new Set(
            activeLangs.filter(l => (perLang[l.code] || 0) > 0).map(l => l.code)
        );
        this._generateAllPhase = 'select';
        this._generateAllResult = null;

        this.openGenerateAllModal();
    }

    openGenerateAllModal() {
        this._generateAllModal = new Modal({
            title: __('Generate All Translations', 'multilingual-press-zone'),
            width: '500px',
            id: 'mpz-generate-all-modal'
        });

        const content = this.renderGenerateAllContent();
        const actions = this.renderGenerateAllActions();

        this._generateAllModal.render(content, actions);
        this._generateAllModal.open();
    }

    renderGenerateAllContent() {
        const activeLangs = this.languages.filter(l => !l.is_default);

        if (this._generateAllPhase === 'progress') {
            const p = this._generateAllProgress || { total: 0, completed: 0, currentLang: null, translated: 0 };
            const currentLangObj = activeLangs.find(l => l.code === p.currentLang);
            const currentLangName = currentLangObj
                ? (currentLangObj.nativeName || currentLangObj.native_name || currentLangObj.name)
                : (p.currentLang || '');
            const pct = p.total > 0 ? Math.round((p.completed / p.total) * 100) : 0;

            const statusText = p.statusText || __('Processing...', 'multilingual-press-zone');

            return el('div', { class: 'mpz-generate-all-modal__progress' },
                // Progress bar
                el('div', { class: 'presszone-multilingual-progress-bar' },
                    el('div', {
                        class: 'presszone-multilingual-progress-bar__fill',
                        style: `width: ${pct}%; transition: width 0.3s ease`
                    }),
                    el('span', { class: 'presszone-multilingual-progress-bar__label' },
                        `${p.completed} / ${p.total}`)
                ),
                // Current language
                el('p', { class: 'mpz-generate-all-modal__title' },
                    p.currentLang
                        ? `${currentLangName}`
                        : __('Preparing...', 'multilingual-press-zone')),
                // Status text — informative about what's happening
                el('p', { class: 'mpz-generate-all-modal__status' }, statusText),
                // Stats
                el('p', { class: 'mpz-generate-all-modal__note' },
                    `${p.completed} ${__('of', 'multilingual-press-zone')} ${p.total} ${__('languages', 'multilingual-press-zone')} · ${p.translated} ${__('strings translated', 'multilingual-press-zone')}`)
            );
        }

        if (this._generateAllPhase === 'done') {
            const result = this._generateAllResult || {};
            const isError = !!result.error;
            const hasFailed = (result.failed || 0) > 0;
            const icon = isError ? '\u2716' : '\u2714';
            const iconClass = isError
                ? 'mpz-generate-all-modal__icon--error'
                : 'mpz-generate-all-modal__icon--success';

            const children = [
                el('span', { class: `mpz-generate-all-modal__icon ${iconClass}` }, icon),
                el('p', { class: 'mpz-generate-all-modal__title' },
                    isError
                        ? __('Translation Failed', 'multilingual-press-zone')
                        : __('Translation Complete', 'multilingual-press-zone')),
            ];

            if (!isError) {
                children.push(el('p', { class: 'mpz-generate-all-modal__note' },
                    `${result.translated || 0} ${__('translated', 'multilingual-press-zone')}` +
                    (hasFailed ? `, ${result.failed} ${__('failed', 'multilingual-press-zone')}` : '')
                ));
            } else {
                children.push(el('p', { class: 'mpz-generate-all-modal__note' }, result.error));
            }

            // Show failure details when available
            const failedDetails = result.failedDetails || [];
            if (failedDetails.length > 0) {
                const failedItems = failedDetails.map(item =>
                    el('div', { class: 'mpz-generate-all-modal__failed-item' },
                        el('span', { class: 'mpz-generate-all-modal__failed-text' },
                            item.text || `#${item.id}`),
                        el('span', { class: 'mpz-generate-all-modal__failed-lang' },
                            item.language || ''),
                        el('span', { class: 'mpz-generate-all-modal__failed-reason' },
                            item.reason || __('Unknown error', 'multilingual-press-zone'))
                    )
                );

                children.push(
                    el('div', { class: 'mpz-generate-all-modal__failed-list' },
                        el('p', { class: 'mpz-generate-all-modal__failed-header' },
                            __('Failed translations:', 'multilingual-press-zone')),
                        ...failedItems
                    )
                );
            }

            return el('div', { class: 'mpz-generate-all-modal__done' }, ...children);
        }

        // 'select' phase — show per-language untranslated counts so the user
        // sees exactly how many strings need translation per language (no double-translation).
        const perLang = this.filterCounts.per_language_untranslated || {};
        const numLangs = this._generateAllSelectedLangs.size;

        // Total strings to translate = sum of per-language untranslated for selected languages
        let totalTranslations = 0;
        for (const code of this._generateAllSelectedLangs) {
            totalTranslations += perLang[code] || 0;
        }

        const langRows = activeLangs.map(lang => {
            const flag = this.getFlagEmoji(lang.flag_code || lang.flagCode || lang.code);
            const displayName = lang.nativeName || lang.native_name || lang.name;
            const isChecked = this._generateAllSelectedLangs.has(lang.code);
            const untranslatedCount = perLang[lang.code] || 0;

            return el('div', {
                class: 'mpz-generate-all-modal__lang-row' + (untranslatedCount === 0 ? ' mpz-generate-all-modal__lang-row--done' : ''),
                onclick: (e) => {
                    // Don't toggle if clicking the checkbox itself (it handles its own change)
                    if (e.target.tagName === 'INPUT') return;
                    if (isChecked) {
                        this._generateAllSelectedLangs.delete(lang.code);
                    } else {
                        this._generateAllSelectedLangs.add(lang.code);
                    }
                    this._generateAllModal.updateContent(this.renderGenerateAllContent());
                    this._generateAllModal.updateActions(this.renderGenerateAllActions());
                }
            },
                Checkbox({
                    checked: isChecked,
                    label: displayName,
                    onChange: (checked) => {
                        if (checked) {
                            this._generateAllSelectedLangs.add(lang.code);
                        } else {
                            this._generateAllSelectedLangs.delete(lang.code);
                        }
                        this._generateAllModal.updateContent(this.renderGenerateAllContent());
                        this._generateAllModal.updateActions(this.renderGenerateAllActions());
                    }
                }),
                el('span', { class: 'mpz-generate-all-modal__flag' }, flag),
                el('span', {}, displayName),
                el('span', { class: 'mpz-generate-all-modal__lang-count' },
                    untranslatedCount > 0
                        ? `${untranslatedCount} ${__('strings', 'multilingual-press-zone')}`
                        : __('fully translated', 'multilingual-press-zone'))
            );
        });

        return el('div', { class: 'mpz-generate-all-modal' },
            el('p', { class: 'mpz-generate-all-modal__summary' },
                totalTranslations > 0
                    ? `${totalTranslations} ${__('translations to generate:', 'multilingual-press-zone')}`
                    : __('All strings are fully translated.', 'multilingual-press-zone')),
            el('div', { class: 'mpz-generate-all-modal__langs' }, ...langRows),
            el('p', { class: 'mpz-generate-all-modal__estimate' },
                `${numLangs} ${numLangs === 1
                    ? __('language selected', 'multilingual-press-zone')
                    : __('languages selected', 'multilingual-press-zone')}`)
        );
    }

    renderGenerateAllActions() {
        if (this._generateAllPhase === 'progress') {
            return [
                Button({
                    label: __('Cancel', 'multilingual-press-zone'),
                    variant: 'ghost',
                    disabled: true
                })
            ];
        }

        if (this._generateAllPhase === 'done') {
            return [
                Button({
                    label: __('Close', 'multilingual-press-zone'),
                    variant: 'primary',
                    onClick: () => {
                        this._generateAllModal.close();
                        this._generateAllModal = null;
                    }
                })
            ];
        }

        // 'select' phase
        return [
            Button({
                label: __('Cancel', 'multilingual-press-zone'),
                variant: 'ghost',
                onClick: () => {
                    this._generateAllModal.close();
                    this._generateAllModal = null;
                }
            }),
            Button({
                label: __('Generate', 'multilingual-press-zone'),
                variant: 'primary',
                disabled: this._generateAllSelectedLangs.size === 0 || (() => {
                    const perLang = this.filterCounts.per_language_untranslated || {};
                    let total = 0;
                    for (const code of this._generateAllSelectedLangs) {
                        total += perLang[code] || 0;
                    }
                    return total === 0;
                })(),
                onClick: () => this.executeGenerateAll()
            })
        ];
    }

    /**
     * Update the Generate All modal content and actions
     */
    _updateGenerateAllModal() {
        if (this._generateAllModal?.modalEl) {
            this._generateAllModal.updateContent(this.renderGenerateAllContent());
            this._generateAllModal.updateActions(this.renderGenerateAllActions());
        }
    }

    async executeGenerateAll() {
        const targetLangs = Array.from(this._generateAllSelectedLangs);

        this._generatingAll = true;
        this._generateAllPhase = 'progress';
        this._generateAllProgress = {
            total: targetLangs.length,
            completed: 0,
            failed: 0,
            currentLang: null,
            translated: 0,
            failedCount: 0,
            failedDetails: [],
            statusText: __('Preparing translation request...', 'multilingual-press-zone'),
        };
        this.updateGenerateAllButton();
        this._updateGenerateAllModal();

        // Build shared filter params
        const baseParams = { type: this.sourceType };
        if (this.filters.source) baseParams.name = this.filters.source;
        if (this.filters.domain) baseParams.domain = this.filters.domain;
        if (this.filters.search) baseParams.search = this.filters.search;
        if (this.filters.status) baseParams.status = this.filters.status;

        // Process one language at a time for progress feedback
        for (const lang of targetLangs) {
            if (this._destroyed) return;

            this._generateAllProgress.currentLang = lang;
            this._generateAllProgress.statusText = __('Sending strings for translation...', 'multilingual-press-zone');
            this._updateGenerateAllModal();

            // Update status to "waiting" after a short delay (the API call may take 30-60s)
            const waitTimer = setTimeout(() => {
                if (this._generateAllProgress.currentLang === lang) {
                    this._generateAllProgress.statusText = __('Translating... waiting for response', 'multilingual-press-zone');
                    this._updateGenerateAllModal();
                }
            }, 3000);

            try {
                const result = await API.post('/system-translate/generate-all', {
                    ...baseParams,
                    target_langs: [lang],
                });

                clearTimeout(waitTimer);
                this._generateAllProgress.translated += (result.translated || 0);
                this._generateAllProgress.failedCount += (result.failed || 0);
                if (result.failed_details) {
                    this._generateAllProgress.failedDetails.push(...result.failed_details);
                }
                this._generateAllProgress.statusText = __('Saving translations...', 'multilingual-press-zone');
                this._updateGenerateAllModal();
            } catch (error) {
                clearTimeout(waitTimer);
                this._generateAllProgress.failed++;
                this._generateAllProgress.failedDetails.push({
                    text: lang,
                    language: lang,
                    reason: error?.message || __('Request failed', 'multilingual-press-zone'),
                });
            }

            this._generateAllProgress.completed++;
            this._generateAllProgress.statusText = this._generateAllProgress.completed < targetLangs.length
                ? __('Moving to next language...', 'multilingual-press-zone')
                : __('Finishing up...', 'multilingual-press-zone');
            this._updateGenerateAllModal();
        }

        // Done
        this._generatingAll = false;
        this._generateAllPhase = 'done';
        this._generateAllResult = {
            translated: this._generateAllProgress.translated,
            failed: this._generateAllProgress.failedCount,
            failedDetails: this._generateAllProgress.failedDetails,
        };
        this.updateGenerateAllButton();
        this._updateGenerateAllModal();

        // Refresh the list immediately so rows/filter counts update
        this.loadStrings({ silent: true });
    }

    destroy() {
        this._destroyed = true;
        if (this.modal) {
            this.modal.close();
            this.modal = null;
        }
        if (this._generateAllModal) {
            this._generateAllModal.close();
            this._generateAllModal = null;
        }
        this.strings = [];
        clear(this.container);
    }
}
